import {
  conversationSteps,
  initialConversationState,
  buildNextStepMessage,
  buildMorePlacesMessage,
  buildShareInterestMessage,
  AMENITY_INSTRUCTION,
} from '../utils/conversationFlow';
import { getPlacesByKeywords, logBotMessage } from '../utils/api';
import { saveConversationToStorage } from '../utils/storage';
import {
  buildStepResponse,
  paginatePlaces,
  nextMessageId,
  replacePlaceholders,
} from '../utils/aiResponseProcessor';
import { MORE_PLACES_HEADER, scheduleBotReply, CONTINUE_TOUR_BUTTON_TEXT } from '../utils/helpers';

const RESTART_DELAY = 100;
const FOLLOW_UP_DELAY = 30000;
const SESSION_ID_RANDOM_MAX = 1000;

const handleInterestToggle = (convState, button) => {
  const hasInterest = convState.selectedInterests.some((i) => i.uuid === button.interest);
  const updatedInterests = hasInterest
    ? convState.selectedInterests.filter((i) => i.uuid !== button.interest)
    : [
      ...convState.selectedInterests,
      { uuid: button.interest, label: button.interestLabel || button.text },
    ];
  return { ...convState, selectedInterests: updatedInterests };
};

const handleAmenityToggle = (convState, amenity, amenityPlaces, onAmenitySelected) => {
  const isCurrentlySelected = convState.selectedAmenities.includes(amenity);
  let updatedAmenities;

  if (isCurrentlySelected) {
    updatedAmenities = convState.selectedAmenities.filter((a) => a !== amenity);
    if (onAmenitySelected) onAmenitySelected(null);
  } else {
    updatedAmenities = [amenity];
    if (onAmenitySelected) {
      const selectedPlace = amenityPlaces.find((place) => place.id === amenity || place.id === String(amenity) || String(place.id) === String(amenity));
      if (selectedPlace) {
        onAmenitySelected({ ...selectedPlace, level_tid: selectedPlace.level_tid || null });
      } else {
        console.error('[TourGuideAi] No place found for amenity ID:', amenity);
        onAmenitySelected({ id: amenity, title: `Amenity ${amenity}` });
      }
    }
  }

  const updatedVisited = convState.visitedAmenities?.includes(amenity)
    ? convState.visitedAmenities
    : [...(convState.visitedAmenities || []), amenity];

  return { ...convState, selectedAmenities: updatedAmenities, visitedAmenities: updatedVisited };
};

const useButtonClick = ({
  state,
  dispatch,
  config,
  handlers: {
    recordActivity,
    scheduleDeferredMessage,
    cancelDeferredMessage,
    flushDeferredMessage,
    handleSessionEnd,
    handleRestartSession,
    handleIdleTimeoutYes,
    handleIdleTimeoutNo,
    saveScrollPosition,
    scrollToBottom,
    handleSideNavToggle,
    initializeConversation,
    onAmenitySelected,
    onExternalLocationFound,
    onResetToCommunity,
    onEnsureZoomZoneZoom,
  },
}) => {
  const { communityNid } = config;

  const handleButtonClick = async (button) => {
    const clickStart = Date.now();
    const {
      conversationState,
      messages,
      sessionId,
      amenityPlaces,
      pendingPlaces,
      pendingPlacesInterests,
    } = state;
    recordActivity();

    const logContext = {
      sessionId,
      selectedInterests: conversationState.selectedInterests.map((i) => i.uuid),
      userInfo: conversationState.userInfo,
      communityNid,
    };

    const SKIP_USER_LOG = ['toggle_interest', 'view_amenity', 'see_external_locations'];
    if (button.text && !SKIP_USER_LOG.includes(button.action)) {
      logBotMessage(`[Clicked <b>${button.text}</b>]`, {
        ...logContext,
        currentStep: conversationState.currentStep,
        questionType: button.action,
      }, 'user');
    }

    let updatedState = { ...conversationState };
    let nextStepId = conversationState.currentStep;

    switch (button.action) {
      case 'toggle_interest': {
        const alreadySelected = updatedState.selectedInterests.some(
          (i) => i.uuid === button.interest,
        );
        if (updatedState.selectedInterests.length >= 3 && !alreadySelected) {
          return;
        }
        updatedState = handleInterestToggle(updatedState, button);

        if (updatedState.selectedInterests.length === 3) {
          nextStepId = 'step4';
          updatedState.currentStep = nextStepId;
          dispatch({ type: 'SET_FLOW_STEP', payload: 3 });
          const interestLabels = updatedState.selectedInterests.map((i) => i.label);
          logBotMessage(`[Selected ${interestLabels.join(', ')}]`, {
            sessionId,
            selectedInterests: updatedState.selectedInterests.map((i) => i.uuid),
            userInfo: updatedState.userInfo,
            communityNid,
            currentStep: nextStepId,
            questionType: 'toggle_interest',
          }, 'user');
        } else {
          dispatch({ type: 'REPLACE_CONVERSATION_STATE', payload: updatedState });
          saveConversationToStorage(sessionId, messages, updatedState, {}, amenityPlaces);
          return;
        }
        break;
      }

      case 'collect_interests':
        nextStepId = button.nextStep;
        updatedState.currentStep = nextStepId;
        break;

      case 'flush_deferred':
        flushDeferredMessage();
        // Defer past the dispatch+render so scrollIntoView lands on the
        // newly appended message and the scroll listener clears the
        // unread count.
        setTimeout(() => {
          if (scrollToBottom) scrollToBottom();
        }, 0);
        return;

      case 'view_amenity': {
        const wasNewlyVisited = !conversationState.visitedAmenities
          ?.map(String).includes(String(button.amenity));
        const isOpening = !conversationState.selectedAmenities?.includes(button.amenity);
        if (isOpening && button.text) {
          logBotMessage(`[Viewed ${button.text}]`, {
            sessionId,
            selectedInterests: conversationState.selectedInterests.map((i) => i.uuid),
            userInfo: conversationState.userInfo,
            communityNid,
            currentStep: conversationState.currentStep,
            questionType: 'view_amenity',
          }, 'user');
        }
        updatedState = handleAmenityToggle(
          updatedState,
          button.amenity,
          amenityPlaces,
          onAmenitySelected,
        );
        dispatch({ type: 'REPLACE_CONVERSATION_STATE', payload: updatedState });
        saveConversationToStorage(sessionId, messages, updatedState, {}, amenityPlaces);

        // When the user has now visited every amenity in the current list,
        // skip the idle wait and surface the next step immediately.
        if (wasNewlyVisited) {
          const lastAmenityMsg = [...messages].reverse().find((m) => (
            m.type === 'bot' && m.buttons?.some((b) => b.action === 'view_amenity')
          ));
          if (lastAmenityMsg) {
            const listIds = lastAmenityMsg.buttons
              .filter((b) => b.action === 'view_amenity')
              .map((b) => String(b.amenity));
            const visitedSet = new Set(
              (updatedState.visitedAmenities || []).map(String),
            );
            const allVisited = listIds.every((id) => visitedSet.has(id));
            if (allVisited) {
              cancelDeferredMessage();
              // For multi-location messages, defer advancing the conversation
              // until the user closes the modal for the final location. The
              // amenityModalOpen effect in TourGuideAi.js handles the trigger.
              if (listIds.length > 1) {
                dispatch({
                  type: 'REPLACE_CONVERSATION_STATE',
                  payload: { ...updatedState, pendingAllVisitedAdvance: true },
                });
                saveConversationToStorage(sessionId, messages, { ...updatedState, pendingAllVisitedAdvance: true }, {}, amenityPlaces);
              } else {
                const firstName = updatedState.userInfo?.firstName || '';
                if (pendingPlaces && pendingPlaces.length > 0) {
                  const moreMsg = buildMorePlacesMessage(pendingPlacesInterests, firstName, config);
                  dispatch({ type: 'APPEND_MESSAGES', payload: [moreMsg] });
                  if (moreMsg?.text) {
                    logBotMessage(moreMsg.text, { ...logContext, currentStep: 'more_places', questionType: 'more_places_prompt' });
                  }
                } else {
                  const nextStep = buildNextStepMessage(firstName, config);
                  if (nextStep) {
                    dispatch({ type: 'APPEND_MESSAGES', payload: [nextStep] });
                    dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
                    logBotMessage(nextStep.text, { ...logContext, currentStep: 'next_step', questionType: 'next_step_prompt' });
                  }
                }
              }
            }
          }
        }
        return;
      }

      case 'next_step':
        nextStepId = button.nextStep;
        updatedState.currentStep = nextStepId;
        break;

      case 'restart_conversation':
        dispatch({ type: 'SET_MESSAGES', payload: [] });
        dispatch({ type: 'REPLACE_CONVERSATION_STATE', payload: { ...initialConversationState } });
        dispatch({ type: 'SET_FLOW_STEP', payload: 1 });
        setTimeout(() => initializeConversation(), RESTART_DELAY);
        return;

      case 'closure_continue': {
        dispatch({ type: 'SET_IS_CLOSURE_FLOW_ACTIVE', payload: false });
        dispatch({ type: 'APPEND_MESSAGES', payload: [{ id: nextMessageId(), type: 'user', text: 'Yes' }] });
        const nextStepMsg = buildNextStepMessage(conversationState?.userInfo?.firstName || '', config);
        if (nextStepMsg) {
          scheduleBotReply(dispatch, () => {
            dispatch({ type: 'APPEND_MESSAGES', payload: [nextStepMsg] });
            dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
            logBotMessage(nextStepMsg.text, { ...logContext, currentStep: 'next_step', questionType: 'next_step_prompt' });
          }, clickStart);
        }
        return;
      }

      case 'closure_end':
        if (onResetToCommunity) onResetToCommunity();
        handleSessionEnd();
        return;

      case 'restart_session':
        handleRestartSession();
        return;

      case 'idle_timeout_yes':
        dispatch({ type: 'APPEND_MESSAGES', payload: [{ id: nextMessageId(), type: 'user', text: "Yes, I'd like to end the tour." }] });
        if (onResetToCommunity) onResetToCommunity();
        handleIdleTimeoutYes();
        return;

      case 'idle_timeout_no':
        dispatch({ type: 'APPEND_MESSAGES', payload: [{ id: nextMessageId(), type: 'user', text: "I'd like to share another interest" }] });
        if (onEnsureZoomZoneZoom) onEnsureZoomZoneZoom();
        handleIdleTimeoutNo();
        return;

      case 'continue_tour':
      case 'ask_question': {
        cancelDeferredMessage();
        nextStepId = button.nextStep;
        updatedState.currentStep = nextStepId;
        updatedState.selectedFollowUpAction = button.action;
        dispatch({ type: 'SET_FLOW_STEP', payload: 4 });
        const followUpStep = conversationSteps[nextStepId];
        const followUpBotResponse = buildStepResponse(followUpStep, updatedState, config, []);
        dispatch({ type: 'REPLACE_CONVERSATION_STATE', payload: updatedState });
        scheduleBotReply(dispatch, () => {
          const followUpMessages = [...messages, followUpBotResponse];
          dispatch({ type: 'SET_MESSAGES', payload: followUpMessages });
          saveConversationToStorage(sessionId, followUpMessages, updatedState, {}, amenityPlaces);
          logBotMessage(followUpBotResponse.text, {
            sessionId,
            selectedInterests: updatedState.selectedInterests.map((i) => i.uuid),
            userInfo: updatedState.userInfo,
            communityNid,
            currentStep: nextStepId,
            questionType: button.action,
          });
        }, clickStart);
        return;
      }

      case 'end_exploration':
        if (onResetToCommunity) onResetToCommunity();
        handleSessionEnd();
        return;

      case 'see_external_locations': {
        if (onExternalLocationFound && button.externalLocations?.length > 0) {
          onExternalLocationFound(button.externalLocations);
        }
        if (button.externalLocations?.length > 0) {
          const extTitles = button.externalLocations
            .map((loc) => loc.title)
            .filter(Boolean);
          if (extTitles.length > 0) {
            logBotMessage(`[Viewed ${extTitles.join(', ')}]`, {
              ...logContext,
              currentStep: conversationState.currentStep,
              questionType: 'see_external_locations',
            }, 'user');
          }
        }
        const locationKey = button.text || 'external';
        if (!updatedState.visitedExternalLocations?.includes(locationKey)) {
          // Defer marking as visited until the map zoom/pan completes — handled
          // by the pendingExternalVisit effect in TourGuideAi.js.
          updatedState = {
            ...updatedState,
            pendingExternalVisit: locationKey,
          };
          dispatch({ type: 'REPLACE_CONVERSATION_STATE', payload: updatedState });
          saveConversationToStorage(sessionId, messages, updatedState, {}, amenityPlaces);
        }
        if (flushDeferredMessage) flushDeferredMessage();
        return;
      }

      case 'close_panel':
        saveScrollPosition();
        handleSideNavToggle();
        return;

      case 'show_more_places': {
        dispatch({ type: 'APPEND_MESSAGES', payload: [{ id: nextMessageId(), type: 'user', text: 'I want to tour more related amenities.' }] });
        const { currentPage: nextPage, remaining: stillPending } = paginatePlaces(pendingPlaces);
        const morePlaceButtons = nextPage.map((place) => ({
          text: place.title,
          action: 'view_amenity',
          amenity: String(place.id),
        }));
        const moreBotMessage = {
          id: nextMessageId() + 1,
          type: 'bot',
          text: replacePlaceholders(MORE_PLACES_HEADER(), { userInfo: { firstName: conversationState?.userInfo?.firstName || '' } }, config),
          instructions: AMENITY_INSTRUCTION,
          buttons: morePlaceButtons,
          stepType: 'ai_places',
          continueButton: { text: CONTINUE_TOUR_BUTTON_TEXT, action: 'flush_deferred' },
        };
        if (stillPending.length > 0) {
          dispatch({ type: 'SET_PENDING_PLACES', payload: { places: stillPending } });
        } else {
          dispatch({ type: 'CLEAR_PENDING_PLACES' });
        }
        scheduleBotReply(dispatch, () => {
          dispatch({ type: 'APPEND_MESSAGES', payload: [moreBotMessage] });
          if (stillPending.length > 0) {
            scheduleDeferredMessage(() => {
              const moreMsg = buildMorePlacesMessage(pendingPlacesInterests, conversationState?.userInfo?.firstName || '', config);
              dispatch({ type: 'APPEND_MESSAGES', payload: [moreMsg], deferred: true });
              if (moreMsg?.text) {
                logBotMessage(moreMsg.text, { ...logContext, currentStep: 'more_places', questionType: 'more_places_prompt' });
              }
            });
          } else {
            scheduleDeferredMessage(() => {
              const nextStep = buildNextStepMessage(conversationState?.userInfo?.firstName || '', config);
              if (nextStep) {
                dispatch({ type: 'APPEND_MESSAGES', payload: [nextStep], deferred: true });
                dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
                logBotMessage(nextStep.text, { ...logContext, currentStep: 'next_step', questionType: 'next_step_prompt' });
              }
            });
          }
        }, clickStart);
        return;
      }

      case 'decline_more_places': {
        cancelDeferredMessage();
        dispatch({ type: 'CLEAR_PENDING_PLACES' });
        dispatch({
          type: 'SET_CONVERSATION_STATE',
          payload: { selectedFollowUpAction: 'decline_more_places' },
        });
        scheduleBotReply(dispatch, () => {
          const shareInterestMsg = buildShareInterestMessage(config);
          dispatch({ type: 'APPEND_MESSAGES', payload: [shareInterestMsg] });
          dispatch({ type: 'SET_FLOW_STEP', payload: 4 });
          dispatch({ type: 'SET_CONVERSATION_STATE', payload: { currentStep: 'share_interest' } });
        }, clickStart);
        return;
      }

      case 'clear_saved_data': {
        localStorage.removeItem('leadInfo');
        const freshSessionId = `session_${Date.now()}${Math.floor(
          Math.random() * SESSION_ID_RANDOM_MAX,
        )}`;
        dispatch({ type: 'SET_SESSION_ID', payload: freshSessionId });
        setTimeout(() => initializeConversation(), RESTART_DELAY);
        return;
      }

      default:
        break;
    }

    const userMessage = {
      id: nextMessageId(),
      type: 'user',
      text: button.text,
      buttonAction: button.action,
    };
    const nextStep = conversationSteps[nextStepId];

    if (nextStep) {
      let places = [];
      if (nextStep.id === 'step4') {
        const response = await getPlacesByKeywords(
          communityNid,
          updatedState.selectedInterests.map((i) => i.uuid),
        );
        places = response.places || [];
        dispatch({ type: 'APPEND_AMENITY_PLACES', payload: places });

        const { remaining: step4Remaining } = paginatePlaces(places);
        if (step4Remaining.length > 0) {
          const interests = updatedState.selectedInterests.map((i) => i.label).join(', ');
          dispatch({ type: 'SET_PENDING_PLACES', payload: { places: step4Remaining, interests } });
        } else {
          dispatch({ type: 'CLEAR_PENDING_PLACES' });
        }
      }

      const botResponse = buildStepResponse(
        nextStep,
        updatedState,
        config,
        places,
      );

      if (nextStep.id === 'step4' && botResponse?.text) {
        const shownTitles = (botResponse.buttons || [])
          .filter((b) => b.action === 'view_amenity')
          .map((b) => b.text)
          .filter(Boolean);
        if (shownTitles.length > 0) {
          logBotMessage(`${botResponse.text} [Returned ${shownTitles.join(', ')}]`, {
            sessionId,
            selectedInterests: updatedState.selectedInterests.map((i) => i.uuid),
            userInfo: updatedState.userInfo,
            communityNid,
            currentStep: nextStepId,
            questionType: 'step4_amenities',
          });
        }
      }

      // Detect the in-place update case (toggle_interest reaching step4 when
      // a step4 message already exists). That path mutates an existing,
      // visible message rather than introducing a new bot message, so it
      // skips the read-time delay.
      const existingStep4Index = (nextStep.id === 'step4' && button.action === 'toggle_interest')
        ? messages.findIndex((msg) => (
          msg.type === 'bot'
          && msg.text
          && msg.text.includes("Here's a selection of amenities we have at")
        ))
        : -1;
      const isInPlaceStep4Update = existingStep4Index !== -1;

      // Show the user's message immediately for instant feedback. The
      // toggle_interest action has no associated user message, and the
      // in-place update case has no new messages at all.
      let messagesWithUser;
      if (isInPlaceStep4Update) {
        messagesWithUser = messages;
      } else if (button.action === 'toggle_interest') {
        messagesWithUser = messages;
      } else {
        messagesWithUser = [...messages, userMessage];
        dispatch({ type: 'SET_MESSAGES', payload: messagesWithUser });
      }
      dispatch({ type: 'REPLACE_CONVERSATION_STATE', payload: updatedState });

      const finalizeBotReply = () => {
        // Add "Continue Tour" for multi-amenity step4 messages.
        if (nextStep.id === 'step4') {
          const amenityBtnCount = (botResponse.buttons || [])
            .filter((b) => b.action === 'view_amenity').length;
          if (amenityBtnCount > 1) {
            botResponse.continueButton = { text: CONTINUE_TOUR_BUTTON_TEXT, action: 'flush_deferred' };
          }
        }

        let finalMessages;
        if (isInPlaceStep4Update) {
          const updatedMessages = [...messagesWithUser];
          updatedMessages[existingStep4Index] = {
            ...updatedMessages[existingStep4Index],
            buttons: botResponse.buttons,
            continueButton: botResponse.continueButton,
          };
          finalMessages = updatedMessages;
        } else {
          finalMessages = [...messagesWithUser, botResponse];
        }

        dispatch({ type: 'SET_MESSAGES', payload: finalMessages });
        saveConversationToStorage(sessionId, finalMessages, updatedState, {}, amenityPlaces);

        if (nextStep.id === 'step4') {
          const { remaining: pendingAfterStep4 } = paginatePlaces(places);
          if (pendingAfterStep4.length > 0) {
            const interests = updatedState.selectedInterests.map((i) => i.label).join(', ');
            scheduleDeferredMessage(() => {
              const moreMsg = buildMorePlacesMessage(interests, updatedState?.userInfo?.firstName || '', config);
              dispatch({ type: 'APPEND_MESSAGES', payload: [moreMsg], deferred: true });
              if (moreMsg?.text) {
                logBotMessage(moreMsg.text, { ...logContext, currentStep: 'more_places', questionType: 'more_places_prompt' });
              }
            });
          } else {
            const followUpStep = buildNextStepMessage(updatedState?.userInfo?.firstName || '', config);
            if (followUpStep) {
              scheduleDeferredMessage(() => {
                dispatch({ type: 'APPEND_MESSAGES', payload: [followUpStep], deferred: true });
                dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
                logBotMessage(followUpStep.text, { ...logContext, currentStep: 'next_step', questionType: 'next_step_prompt' });
              }, FOLLOW_UP_DELAY);
            }
          }
        }
      };

      if (isInPlaceStep4Update) {
        finalizeBotReply();
      } else {
        scheduleBotReply(dispatch, finalizeBotReply, clickStart);
      }
    }
  };

  return { handleButtonClick };
};

export default useButtonClick;
